import client from './client';
import {
  MiscTaskActionResult,
  MiscTaskApproveRequest,
  MiscTaskCounts,
  MiscTaskHistoryEntry,
  MiscTaskQueue,
  MiscTaskQueueResponse,
} from '../interfaces/MiscTaskApprovalInterface';

/**
 * NPM Reds two-level approval — axios wrappers for /admin/miscellaneousTasks
 * (TAS-358). Response shapes are documented on the interfaces in
 * interfaces/MiscTaskApprovalInterface.ts.
 *
 * These rethrow the ORIGINAL axios error rather than wrapping it, following the
 * api/timesheetEdit convention: 403 (not your red / outside your scope) and 409
 * `no-slot` (authorized, but you hold no unsatisfied slot at that level) are
 * DESIGNED states the page renders inline, not failures to swallow. Callers read
 * `err.response.status` and `err.response.data.errorCode`.
 *
 * The legacy api/miscellaneousTasks module is deliberately left alone — it
 * swallows status codes and raises alerts from the api layer, and the v1 page
 * still depends on both behaviours.
 */

const BASE = '/admin/miscellaneousTasks';

/**
 * GET /getForPayPeriod — one bucket of reds plus the viewer block.
 *
 * `queue` is ALWAYS sent. Omitting it makes the same route answer with a legacy
 * bare array (the v1 back-compat shim), which has no approvalStatus, no viewer
 * and no server-side scoping — so this function has no overload without it.
 */
export const getMiscTaskQueue = async (
  payPeriodID: number,
  queue: MiscTaskQueue
): Promise<MiscTaskQueueResponse> => {
  try {
    const response = await client.get(`${BASE}/getForPayPeriod`, {
      params: { payPeriodID, queue },
    });
    return response.data as MiscTaskQueueResponse;
  } catch (error) {
    console.error(`Error fetching misc task queue (${queue}):`, error);
    throw error;
  }
};

/**
 * GET /counts — the four stage-card numbers plus the unroutable count.
 *
 * Under server-side scoping the client cannot derive any of these from the rows
 * it can see, so every number on the page comes from here.
 */
export const getMiscTaskCounts = async (payPeriodID: number): Promise<MiscTaskCounts> => {
  try {
    const response = await client.get(`${BASE}/counts`, { params: { payPeriodID } });
    return response.data as MiscTaskCounts;
  } catch (error) {
    console.error('Error fetching misc task counts:', error);
    throw error;
  }
};

/** GET /:id/history — the audit trail, newest first. */
export const getMiscTaskHistory = async (
  miscEmployeeTaskID: number
): Promise<MiscTaskHistoryEntry[]> => {
  try {
    const response = await client.get(`${BASE}/${miscEmployeeTaskID}/history`);
    return (response.data as MiscTaskHistoryEntry[]) ?? [];
  } catch (error) {
    console.error('Error fetching misc task history:', error);
    throw error;
  }
};

/**
 * POST /:id/approve — record this caller's approval at `level`.
 *
 * Level 2 requires `superDescription` (the 60 characters that print to Vista).
 * `comments` is optional at both levels (D6/D22). The acting admin always comes
 * from the session; it is never sent in the body.
 */
export const approveMiscTask = async (
  miscEmployeeTaskID: number,
  request: MiscTaskApproveRequest
): Promise<MiscTaskActionResult> => {
  try {
    const response = await client.post(`${BASE}/${miscEmployeeTaskID}/approve`, request);
    return response.data as MiscTaskActionResult;
  } catch (error) {
    console.error('Error approving misc task:', error);
    throw error;
  }
};

/** POST /:id/deny — terminal. Zeroes the amount, keeping the original. */
export const denyMiscTask = async (
  miscEmployeeTaskID: number,
  reason: string
): Promise<MiscTaskActionResult> => {
  try {
    const response = await client.post(`${BASE}/${miscEmployeeTaskID}/deny`, { reason });
    return response.data as MiscTaskActionResult;
  } catch (error) {
    console.error('Error denying misc task:', error);
    throw error;
  }
};

/**
 * POST /:id/forceApprove — the OverrideMiscTaskApproval escape hatch. Satisfies
 * every outstanding slot at once and is logged as FORCE_APPROVE, never as a
 * normal approval.
 */
export const forceApproveMiscTask = async (
  miscEmployeeTaskID: number,
  superDescription: string,
  comments?: string
): Promise<MiscTaskActionResult> => {
  try {
    const response = await client.post(`${BASE}/${miscEmployeeTaskID}/forceApprove`, {
      superDescription,
      ...(comments ? { comments } : {}),
    });
    return response.data as MiscTaskActionResult;
  } catch (error) {
    console.error('Error force-approving misc task:', error);
    throw error;
  }
};

/**
 * PUT /:id — correct the Vista description on an already-approved red. An
 * oversight correction: `approvalStatus` is unchanged and the trail records both
 * the old and the new text.
 */
export const correctMiscTaskDescription = async (
  miscEmployeeTaskID: number,
  superDescription: string
): Promise<MiscTaskActionResult> => {
  try {
    const response = await client.put(`${BASE}/${miscEmployeeTaskID}`, { superDescription });
    return response.data as MiscTaskActionResult;
  } catch (error) {
    console.error('Error correcting misc task description:', error);
    throw error;
  }
};

// ── Error readers ────────────────────────────────────────────────────────────
// The rejection paths are part of the design, so the page needs the status and
// the errorCode, not just a message. These keep the `any` casts in one place.

interface AxiosLikeError {
  response?: { status?: number; data?: { message?: string; errorCode?: string } };
  message?: string;
}

/** HTTP status of a rethrown axios error, or null if it never reached the server. */
export const getMiscTaskErrorStatus = (error: unknown): number | null =>
  (error as AxiosLikeError)?.response?.status ?? null;

/** The state machine's `errorCode` on a 409 — `no-slot`, `already-approved`, … */
export const getMiscTaskErrorCode = (error: unknown): string | null =>
  (error as AxiosLikeError)?.response?.data?.errorCode ?? null;

/** The server's own sentence, which is written for the user. */
export const getMiscTaskErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as AxiosLikeError;
  return e?.response?.data?.message || e?.message || fallback;
};
